<template>
  <div v-if="visible && note" class="modal-overlay">
    <div class="modal-content">
      <div class="modal-icon">
        <i class="el-icon-warning-outline"></i>
      </div>
      
      <h2 class="modal-title">Delete Note</h2>
      
      <p class="modal-message">
        Are you sure you want to delete <span class="note-name">"{{ note.notename }}"</span>?
      </p>
      <p class="modal-warning">This action cannot be undone. All content and recordings will be permanently deleted.</p>
      
      <div class="modal-actions">
        <button class="cancel-button" @click="closeModal" :disabled="isDeleting">
          Cancel
        </button>
        <button class="delete-button" @click="handleDelete" :disabled="isDeleting">
          <span class="loading-spinner" v-if="isDeleting"></span>
          <span v-else>Delete</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PopupNoteDeleteConfirm',
  props: {
    note: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      visible: false,
      isDeleting: false
    }
  },
  methods: {
    openModal() {
      if (!this.note) return
      this.visible = true
    },
    closeModal() {
      this.visible = false
    },
    async handleDelete() {
      try {
        this.isDeleting = true
        const response = await this.$axios.post("/api/deletenote/", {
          noteid: this.note.noteid,
          subid: this.$cookies.get("subid"),
        });

        if (response.data.code === 200) {
          this.$emit('confirm', this.note)
          this.$message.success('Note deleted successfully')
          this.closeModal()
        } else {
          this.$message.error(response.data.messages || 'Failed to delete note')
        }
      } catch (error) {
        console.error('Error deleting note:', error)
        this.$message.error(error.response?.data?.messages || 'Failed to delete note')
      } finally {
        this.isDeleting = false
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #ffffff;
  border-radius: 20px;
  padding: 30px;
  width: 90%;
  max-width: 600px;
  position: relative;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  text-align: center;
  animation: fadeIn 0.3s ease, slideIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(30px);
  }
  to {
    transform: translateY(0);
  }
}

.modal-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background-color: #FFF3F3;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 24px;
}

.modal-icon i {
  font-size: 38px;
  color: #FF4D4F;
}

.modal-title {
  color: #1A1A1A;
  font-size: 24px;
  margin: 0 0 16px 0;
  font-weight: 700;
}

.modal-message {
  color: #4A4A4A;
  font-size: 16px;
  margin-bottom: 12px;
  line-height: 1.5;
}

.note-name {
  font-weight: 600;
  color: #1A1A1A;
}

.modal-warning {
  color: #FF4D4F;
  font-size: 14px;
  margin-bottom: 32px;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.cancel-button {
  padding: 12px 24px;
  border-radius: 12px;
  border: 1px solid #E0E0E0;
  background-color: #FFFFFF;
  color: #4A4A4A;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
}

.cancel-button:hover:not(:disabled) {
  background-color: #F5F5F5;
  border-color: #D0D0D0;
  transform: translateY(-2px);
}

.cancel-button:active:not(:disabled) {
  transform: translateY(0);
}

.delete-button {
  padding: 12px 24px;
  border-radius: 12px;
  border: none;
  background-color: #FF4D4F;
  color: white;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  box-shadow: 0 4px 12px rgba(255, 77, 79, 0.25);
  display: flex;
  justify-content: center;
  align-items: center;
}

.delete-button:hover:not(:disabled) {
  background-color: #FF7875;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(255, 77, 79, 0.35);
}

.delete-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(255, 77, 79, 0.25);
}

.cancel-button:disabled,
.delete-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style> 